import { useRef, useState } from "react"
import { ImageIcon } from "lucide-react"
import { ulid } from "ulid"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { DropZone } from "@/components/ui/drop-zone"
import { supabase } from "@/lib/agents/db"

const BUCKET_NAME = "static"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  image?: string
  onChange: (image: string | undefined) => void
}

export function ImageDialog({ open, onOpenChange, image, onChange }: Props) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  async function upload(files: File[]) {
    const file = files[0]
    if (!file) return
    if (!file.type.startsWith("image/")) return setError("Only images can be attached")
    setUploading(true)
    setError(null)
    const { data, error } = await supabase.storage.from(BUCKET_NAME).upload(`${ulid()}-${file.name}`, file, { cacheControl: "3600", upsert: true })
    setUploading(false)
    if (error || !data?.path) return setError(error?.message || "Upload failed")
    onChange(supabase.storage.from(BUCKET_NAME).getPublicUrl(data.path).data.publicUrl)
  }

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Image</DialogTitle>
        <DialogDescription>Sent with the message, which becomes its caption. One image per item.</DialogDescription>
      </DialogHeader>
      <DropZone onFilesDrop={upload} className="w-full">
        <button type="button" disabled={uploading} onClick={() => input.current?.click()} className="flex min-h-48 w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed p-4 hover:bg-muted/50">
          {image ? <img src={image} alt="" className="max-h-64 max-w-full rounded object-contain" /> : <>
            <ImageIcon className="size-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Drop an image here, or click to select one</p>
          </>}
          {uploading && <p className="text-sm font-medium">Uploading...</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </button>
      </DropZone>
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={e => { upload(Array.from(e.target.files ?? [])); e.target.value = "" }} />
      {image && <DialogFooter>
        <Button variant="outline" disabled={uploading} onClick={() => onChange(undefined)}>Remove</Button>
      </DialogFooter>}
    </DialogContent>
  </Dialog>
}
